import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import Typography from 'app/ui/components/atoms/typography';
import CollectionCard from 'src/app/ui/components/molecules/gradient-action-card';

import type { Collection } from 'app/types/graphql';
import { routes } from 'app/routes/constants';

import EmptyState from 'app/ui/components/molecules/empty-state';
import GhostIcon from 'app/ui/components/atoms/ghost';

const CollectionsArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
  gap: 2.5rem;
  margin-bottom: 6rem;
`;

const CollectionCardWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  width: 100%;
  align-items: stretch;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        grid-template-columns: repeat(2, 1fr);
      `,
    )}
  `}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
`;

const PageTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

type CollectionListProps = {
  collections: Collection[];
  title: string;
  description: string;
  emptyStateTitle: string;
};

export const CollectionList: React.FC<CollectionListProps> = ({ collections, title, description, emptyStateTitle }) => {
  const theme = useTheme();

  return (
    <CollectionsArea>
      <TitleWrapper>
        <PageTitle variant="h3" color={theme.colors.white}>
          {title}
        </PageTitle>
        <Typography variant="body2" color={theme.colors.lightGray}>
          {description}
        </Typography>
      </TitleWrapper>
      {collections.length > 0 ? (
        <CollectionCardWrapper>
          {collections.map(({ title, id, ctaImage, shortDescription }) => (
            <CollectionCard
              key={id}
              title={title}
              shortDescription={shortDescription || ''}
              ctaImageUrl={ctaImage?.url || ctaImage?.signedUrl}
              to={routes.COLLECTION.replace(':id', id)}
            />
          ))}
        </CollectionCardWrapper>
      ) : (
        <EmptyState icon={<GhostIcon />} heading={emptyStateTitle} />
      )}
    </CollectionsArea>
  );
};

type LibraryCollectionsProps = {
  collections: Collection[];
};

const LibraryCollections: React.FC<LibraryCollectionsProps> = ({ collections }) => {
  const intl = useIntl();

  return (
    <CollectionList
      collections={collections}
      title={intl.formatMessage({ id: 'library.collections' })}
      description={intl.formatMessage({ id: 'library.collectionsCopy' })}
      emptyStateTitle={intl.formatMessage({ id: 'library.noCollections' })}
    />
  );
};

export default LibraryCollections;
